import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Empty, Input, Space, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { InfoCircleOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { queryKeys } from '@/api/queryKeys'
import { useAdvisoryAiOrgs } from '@/features/advisory-ai/hooks'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useToast } from '@/contexts/ToastContext'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { resolveEnvironmentName } from '@/lib/environment'
import { updateSession, useAuthSession } from '@/lib/session'
import { permissions, UserTypes } from '@/types/authorization'
import type { AdvisoryAiOrgRow } from '@/features/advisory-ai/types'
import { formatDateTime, platformRefreshStatusTagColor } from '@/features/advisory-ai/utils'
import { refreshStatusColumnHelp } from './constants'
import { useXeroBulkSsoConnect } from './useXeroBulkSsoConnect'
import { useAdvicePrepTrialLimit } from '@/features/adviceprep/hooks'
import AdvicePrepTrialLimitAlert from '@/features/adviceprep/AdvicePrepTrialLimitAlert'
import { advicePrepTrialLimitHint } from '@/features/adviceprep/trialLimit'

const XERO_ACCOUNTING_TYPE_ID = 1

export default function AdvisoryAI() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const { addToast } = useToast()
  const { permissions: userPermissions } = useAuthorization()
  const clientId = session?.currentClientId || ''
  const isAdmin = session?.currentUserTypeId === UserTypes.Admin
  const canAddPlatform = userPermissions.has(permissions.addPlatform)
  // Only the button this gates needs it, so a user who cannot add a connection
  // makes neither of the hook's requests.
  const advicePrepTrial = useAdvicePrepTrialLimit(clientId, { enabled: canAddPlatform })
  const envName = resolveEnvironmentName()
  const defaultPageSize = session?.defaultPageSize && session.defaultPageSize > 0 ? session.defaultPageSize : 20

  const [companyNameFilter, setCompanyNameFilter] = useState('')
  const orgFilters = {
    clientId,
    companyName: companyNameFilter,
    typeId: XERO_ACCOUNTING_TYPE_ID,
  }

  const { open: openBulkSso, isPolling } = useXeroBulkSsoConnect({
    clientId,
    userId: session?.currentUserId ?? '',
    envName,
    orgsQueryKey: queryKeys.advisory.aiOrgs(orgFilters),
  })

  const openXeroConnect = () => {
    switch (openBulkSso()) {
      case 'no-user':
        addToast('Session missing user ID', 'error')
        return
      case 'blocked':
        addToast('Popup blocked. Allow popups and retry.', 'error')
        return
      case 'opened':
        addToast('Complete authentication in the popup window.', 'info')
    }
  }

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: defaultPageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  })

  const query = useAdvisoryAiOrgs(orgFilters)

  const columns: ColumnsType<AdvisoryAiOrgRow> = useMemo(
    () => [
      {
        title: (
          // data-tour anchors the assistant walkthroughs — see assistant/mockData.ts
          <span data-tour="org-search" className="block">
            <Input
              size="small"
              value={companyNameFilter}
              onChange={(e) => setCompanyNameFilter(e.target.value)}
              placeholder="Company Name"
              suffix={<SearchOutlined />}
              allowClear
            />
          </span>
        ),
        dataIndex: 'companyName',
        key: 'companyName',
        render: (value: string, row) => (
          <a
            data-tour="org-open"
            className="feature-link font-medium"
            onClick={() => navigate(`/adviceprep/org/${row.companyId}/calls`)}
          >
            {value}
          </a>
        ),
      },
      {
        title: 'Platform Type',
        dataIndex: 'platformTypeLabel',
        key: 'platformTypeLabel',
      },
      {
        title: (
          <Space size={6}>
            <span>Refresh Status</span>
            <Tooltip title={refreshStatusColumnHelp}>
              <InfoCircleOutlined />
            </Tooltip>
          </Space>
        ),
        dataIndex: 'refreshStatus',
        key: 'refreshStatus',
        render: (value: string) => <Tag color={platformRefreshStatusTagColor(value)}>{value}</Tag>,
      },
      {
        title: 'Last Refreshed',
        dataIndex: 'lastSuccessfulRefresh',
        key: 'lastSuccessfulRefresh',
        render: (value: string | null) => (value ? <a className="feature-link">{formatDateTime(value)}</a> : null),
      },
    ],
    [companyNameFilter, navigate]
  )

  if (!clientId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="No active client selected"
        description="Select a client to view AdvicePrep organisations."
      />
    )
  }

  return (
    <FeatureThemeProvider featureKey="advisoryIntel" withAntd className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4">
        <h1 className="font-display text-2xl font-bold text-gray-900">AdvicePrep</h1>
        {/* Secondary actions first, the primary one last. Wraps rather than
            overflowing on a narrow viewport. */}
        <div className="flex flex-wrap items-center justify-end gap-4" data-tour="org-actions">
          {/* Admins only. Hiding the button is not the gate — the route
              redirects a typed URL and the server answers 403. */}
          {isAdmin && (
            <Button className="feature-button-outline" onClick={() => navigate('/adviceprep/support')}>
              Support Console
            </Button>
          )}
          <Button className="feature-button-outline" onClick={() => navigate('/adviceprep/consolidated')}>
            Consolidated Analysis
          </Button>
          <Button className="feature-button-outline" onClick={() => navigate('/adviceprep/settings')}>
            Settings
          </Button>
          {/* Left out of the spinner while the bulk-SSO poll runs: it refetches
              this same query, and a button blinking through it reads as broken. */}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void query.refetch()}
            loading={query.isFetching && !isPolling}
          >
            Refresh
          </Button>
          {/* Every row here is a Xero connection, so the action that creates
              one belongs on this screen too. Gated on `addPlatform`. */}
          {canAddPlatform && clientId && (
            <Tooltip
              title={
                advicePrepTrial.atLimit
                  ? advicePrepTrialLimitHint
                  : 'Open Xero to connect one or more organisations to this client'
              }
            >
              <Button
                type="primary"
                data-tour="add-xero"
                className="feature-button-solid"
                icon={<PlusOutlined />}
                disabled={advicePrepTrial.atLimit}
                onClick={openXeroConnect}
              >
                Add Xero Connection
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {advicePrepTrial.atLimit && (
        <div className="pb-4">
          <AdvicePrepTrialLimitAlert trialEnd={advicePrepTrial.trialEnd} noun="organisations" />
        </div>
      )}

      {query.error ? (
        <div className="pb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load AdvicePrep organisations"
            description={query.error instanceof Error ? query.error.message : 'Unknown error'}
          />
        </div>
      ) : null}

      <div data-tour="org-table">
        <Table<AdvisoryAiOrgRow>
          rowKey="id"
          columns={columns}
          dataSource={query.data || []}
          loading={query.isLoading || (query.isFetching && !isPolling)}
          pagination={{
            ...pagination,
            onChange: (current, pageSize) => {
              setPagination((prev) => ({ ...prev, current, pageSize }))
              if (session && pageSize && pageSize !== session.defaultPageSize) {
                updateSession({ defaultPageSize: pageSize })
              }
            },
          }}
          locale={{
            emptyText: query.isLoading ? 'Loading...' : <Empty description="No advisory organisations found" />,
          }}
        />
      </div>
    </FeatureThemeProvider>
  )
}
